/* eslint-disable func-names */
import axios, { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import jwtDecode from 'jwt-decode';
import { SigninSilentArgs, User } from 'oidc-client-ts';

interface IRetryQueueProps {
  config: AxiosRequestConfig | undefined;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

class API {
  public instance: AxiosInstance;

  private isRefreshingRequestToken: boolean;

  private isRefreshingResponseToken: boolean;

  private refreshAndRetryRequestQueue: IRetryQueueProps[];

  private refreshAndRetryResponseQueue: IRetryQueueProps[];

  constructor() {
    this.instance = axios.create({
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
      }
    });
    this.refreshAndRetryRequestQueue = [];
    this.refreshAndRetryResponseQueue = [];
    this.isRefreshingRequestToken = false;
    this.isRefreshingResponseToken = false;
  }

  setRequestInterceptors(
    navigate: any,
    signinSilent: (args?: SigninSilentArgs) => Promise<any | null>,
    removeUser: () => void,
    signoutSilent: () => void,
    clearUserFromMemory: () => void
  ) {
    this.instance.interceptors.request.use(
      async (request: any) => {
        if (!request) throw new Error('Invalid request config');

        // Pull user profile from session storage and grab access token
        const user = User.fromStorageString(
          sessionStorage.getItem(
            `oidc.user:${process.env.REACT_APP_SSO_URL}:${process.env.REACT_APP_SSO_OAUTH_CLIENT_ID}`
          ) ?? '{}'
        );
        const currentAccessToken = user.access_token;
        // If token exists add to the request headers Authorization header else remove said header
        if (currentAccessToken) {
          const decodedToken: any = jwtDecode(currentAccessToken);
          const expiration = decodedToken.exp;
          if (expiration && new Date().valueOf() - expiration * 1000 <= 0) {
            const authToken = `bearer ${currentAccessToken}`;
            request.headers.Authorization = authToken;
            return request;
          }
          // If a 401 token expired occurs from server start the refresh process unless its already being refreshed
          if (!this.isRefreshingRequestToken) {
            // Turn on queueing
            this.isRefreshingRequestToken = true;

            console.info('Refreshing token');

            try {
              const { accessToken: newAccessToken } = await signinSilent();
              const authToken = `bearer ${newAccessToken}`;
              request.headers.Authorization = authToken;

              // Run through any pending requests and re-submit with the new token
              this.refreshAndRetryRequestQueue.forEach(
                ({ config, resolve, reject }) => {
                  const updatedConfig = config;
                  if (updatedConfig && updatedConfig.headers) {
                    updatedConfig.headers.Authorization = authToken;
                    this.instance
                      .request(updatedConfig)
                      .then(response => resolve(response))
                      .catch(err => reject(err));
                  }
                }
              );

              // Clear the queue
              this.refreshAndRetryRequestQueue.length = 0;
              // Re-submit the original failed request
              return request;
            } catch (err) {
              // If any failure occurs from the refresh cycle clear the user from the app and navigate to 401 page
              console.error('Failed refreshing token');
              console.error(err);
              clearUserFromMemory();
              removeUser();
              signoutSilent();
              navigate('/401');
            } finally {
              // Turn off queueing
              this.isRefreshingRequestToken = false;
            }
          }

          // Push any pending async request to the queue if a refresh cycle has started
          return new Promise((resolve, reject) => {
            this.refreshAndRetryRequestQueue.push({
              config: request,
              resolve,
              reject
            });
          });
        }

        request.headers.delete('Authorization');

        // Clear all user info from app if cant find token and navigate to 401 page
        clearUserFromMemory();
        removeUser();
        signoutSilent();

        return Promise.reject(new Error('Invalid token specified'));
      },
      error => {
        // If any errors occur clear all user info from app
        if (error.message.includes('Invalid token specified')) {
          console.error(error.message);
          clearUserFromMemory();
          removeUser();
          signoutSilent();
          return navigate('/401');
        }
        console.error(error.message);
        return Promise.reject(error);
      }
    );
  }

  setResponseInterceptors(
    navigate: any,
    signinSilent: (args?: SigninSilentArgs) => Promise<any | null>,
    removeUser: () => void,
    signoutSilent: () => void,
    clearUserFromMemory: () => void
  ) {
    this.instance.interceptors.response.use(
      function (response) {
        return response;
      },
      async error => {
        const originalRequest = error.config;
        const { response: failedResponse } = error;
        // Handle error page redirects by checking all api call rejections
        if (failedResponse && failedResponse.config.url.indexOf('api') > 0) {
          if (failedResponse.status === 401) {
            if (!this.isRefreshingResponseToken) {
              this.isRefreshingResponseToken = true;
              // Pull user profile from session storage and grab access token
              const user = User.fromStorageString(
                sessionStorage.getItem(
                  `oidc.user:${process.env.REACT_APP_SSO_URL}:${process.env.REACT_APP_SSO_OAUTH_CLIENT_ID}`
                ) ?? '{}'
              );
              let currentAccessToken = user.access_token;
              console.log('Refreshing token');

              try {
                const { accessToken: newAccessToken } = await signinSilent();
                currentAccessToken = newAccessToken;
                const authToken = `bearer ${currentAccessToken}`;
                // eslint-disable-next-line no-param-reassign
                originalRequest.headers.Authorization = authToken;

                this.refreshAndRetryResponseQueue.forEach(
                  ({ config, resolve, reject }) => {
                    const updatedConfig = config;
                    if (updatedConfig && updatedConfig.headers) {
                      updatedConfig.headers.Authorization = authToken;
                      this.instance
                        .request(updatedConfig)
                        .then(response => resolve(response))
                        .catch(err => reject(err));
                    }
                  }
                );

                // Clear the queue
                this.refreshAndRetryResponseQueue.length = 0;
                return await this.instance(originalRequest);
              } catch (err) {
                console.log('Failed refreshing token', err);
                clearUserFromMemory();
                removeUser();
                signoutSilent();
                navigate('/401');
              } finally {
                this.isRefreshingResponseToken = false;
              }
            }
            return new Promise((resolve, reject) => {
              this.refreshAndRetryResponseQueue.push({
                config: originalRequest,
                resolve,
                reject
              });
            });
          }
          if (failedResponse.status === 403) {
            // If forbidden statuses are thrown send to error screen
            clearUserFromMemory();
            removeUser();
            signoutSilent();
            return navigate('/403');
          }
          if (failedResponse.status === 404) {
            // Only on auth endpoint if not authorized show error screnn
            if (originalRequest.url.indexOf('user/auth') > 0) {
              clearUserFromMemory();
              removeUser();
              signoutSilent();
              return navigate('/404');
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  apiCall({
    method,
    url,
    data = {}
  }: {
    method?: Method;
    url: string;
    data: any;
  }) {
    return this.instance({ method, url, data });
  }
}

const api = new API();

const getData = async (url: string, data: any) => {
  try {
    return await api.apiCall({ method: 'get', url, data });
  } catch (error) {
    return { error };
  }
};

const postData = async (url: string, data: any) => {
  try {
    return await api.apiCall({ method: 'post', url, data });
  } catch (error) {
    return { error };
  }
};

const putData = async (url: string, data: any) => {
  try {
    return await api.apiCall({ method: 'put', url, data });
  } catch (error) {
    return { error };
  }
};

const deleteData = async (url: string, data: any) => {
  try {
    return await api.apiCall({ method: 'delete', url, data });
  } catch (error) {
    return { error };
  }
};

const patchData = async (url: string, data: any) => {
  try {
    return await api.apiCall({ method: 'patch', url, data });
  } catch (error) {
    return { error };
  }
};

const downloadFile = (url: string) =>
  api.instance.request({
    url,
    method: 'get',
    responseType: 'blob'
  });

export { getData, postData, putData, deleteData, patchData, downloadFile };

export default api;
